import { Badge } from "@shared/ui/badge";
import { Switch } from "@shared/ui/switch";
import { Edit, Trash2, Eye } from "lucide-react";
import { GenericTable } from "@shared/components/GenericTable"; 
import { TableColumn } from "@shared/types/table";
import { useUpdateFaqStatus, useDeleteFaq, useDashboardDetails } from "../../hooks";
import { UpdateFAQStatusPayload } from "../../types";
import { getRemoteUtilityId } from "@shared/utils/getUtilityId";
import { useMemo, useState, useEffect } from "react";
import { Button } from "@shared/ui/button";
import { Label } from "@shared/ui/label";
import { Select, SelectContent, SelectGroup, SelectItem, SelectTrigger, SelectValue } from "@shared/ui/select";

interface FAQ {
  id: string;
  question: string;
  answer: string;
  category: string;
  utility: string;
  status: "active" | "inactive";
  order: number;
}

interface FAQFilterPanelProps {
  currentFilters: {
    category: string;
  };
  onApplyFilters: (filters: { category?: string }) => void;
  onResetFilters: () => void;
  categoryOptions: string[];
}

const FAQFilterPanel: React.FC<FAQFilterPanelProps> = ({
  currentFilters,
  onApplyFilters,
  onResetFilters,
  categoryOptions,
}) => {
  const [tempFilters, setTempFilters] = useState({
    category: currentFilters.category || '',
  });

  useEffect(() => {
    setTempFilters({
      category: currentFilters.category || '',
    });
  }, [currentFilters]);

  const handleCategoryChange = (value: string) => {
    setTempFilters((prev) => ({ ...prev, category: value === 'ALL_CATEGORIES' ? '' : value }));
  };

  const handleApply = () => {
    const filtersToApply: { category?: string } = {};
    
    if (tempFilters.category) {
      filtersToApply.category = tempFilters.category;
    }
    
    onApplyFilters(filtersToApply);
  };

  const handleReset = () => {
    setTempFilters({ category: '' });
    onResetFilters();
  };

  return (
    <form onSubmit={e => { e.preventDefault(); handleApply(); }}>
      <div className="grid grid-cols-1 gap-6">
        <div className="space-y-2">
          <Label>Category</Label>
          <Select 
            value={tempFilters.category || 'ALL_CATEGORIES'} 
            onValueChange={handleCategoryChange}
          >
            <SelectTrigger>
              <SelectValue placeholder="Select category" />
            </SelectTrigger>
            <SelectContent>
              <SelectGroup>
                <SelectItem value="ALL_CATEGORIES">All Categories</SelectItem>
                {categoryOptions.map((category: string) => (
                  <SelectItem key={category} value={category}>
                    {category}
                  </SelectItem>
                ))}
              </SelectGroup>
            </SelectContent>
          </Select>
        </div>

        <div className="flex justify-end gap-2">
          <Button type="button" variant="outline" onClick={handleReset}>
            Reset Filters
          </Button>
          <Button type="submit">
            Apply Filters
          </Button>
        </div>
      </div>
    </form>
  );
};

interface FAQTableProps {
  faqs: FAQ[];
  onView: (faq: FAQ) => void;
  onEdit: (faq: FAQ) => void;
  onDelete: (id: string) => void;
  onToggleStatus?: (faq: FAQ) => void;
  onRefetch?: () => void;
  // Search and pagination props
  searchPlaceholder?: string;
  searchParamName?: string;
  searchDebounceMs?: number;
  onSearch?: (searchTerm: string, paramName: "search_data") => void;
  // Filter props
  currentFilters?: {
    category: string;
  };
  onApplyFilters?: (filters: { category?: string }) => void;
  onResetFilters?: () => void;
  categoryOptions?: string[];
  pagination?: {
    currentPage: number;
    totalPages: number;
    totalItems: number;
    itemsPerPage: number;
    startItem: number;
    endItem: number;
  };
  onPageChange?: (newPage: number) => void;
}

export const FAQTable = ({ 
  faqs, 
  onView, 
  onEdit, 
  onDelete, 
  onToggleStatus, 
  onRefetch,
  searchPlaceholder,
  searchParamName,
  searchDebounceMs,
  onSearch,
  currentFilters,
  onApplyFilters,
  onResetFilters,
  categoryOptions = [],
  pagination,
  onPageChange
}: FAQTableProps) => {
  const remoteUtilityId = getRemoteUtilityId();
  
  // Dashboard details hook
  const dashboardDetailsParams = useMemo(() => ({
    remote_utility_id: remoteUtilityId,
  }), [remoteUtilityId]);
  
  const { mutate: updateFaqStatus, isPending: isUpdatingStatus } = useUpdateFaqStatus();
  const { mutate: deleteFaq, isPending: isDeletingFaq } = useDeleteFaq();
  const { refetch: refetchDashboardDetails } = useDashboardDetails(dashboardDetailsParams);

  const handleToggleStatus = (faq: FAQ) => {
    const payload: UpdateFAQStatusPayload = {
      code: faq.id,
      is_active: faq.status === "inactive",
      remote_utility_id: remoteUtilityId
    };

    updateFaqStatus(payload, {
      onSuccess: () => {
        onToggleStatus?.(faq);
        onRefetch?.();
        refetchDashboardDetails(); // Refetch dashboard details after successful FAQ status update
      },
      onError: (error) => {
        console.error('Failed to update FAQ status:', error);
      }
    });
  };

  const handleDelete = (faq: FAQ) => {
    const payload = {
      type:"faq",
      remote_utility_id: remoteUtilityId,
      code: faq.id
    };

    deleteFaq(payload, {
      onSuccess: () => {  
        onDelete(faq.id);
        onRefetch?.();
        refetchDashboardDetails(); // Refetch dashboard details after successful FAQ deletion
      },
      onError: (error) => {
        console.error('Failed to delete FAQ:', error);
      }
    });
  };

  // Define table columns
  const columns: TableColumn<FAQ>[] = [
    {
      key: "question",
      header: "Question",
      sortable: true,
    },
    {
      key: "category",
      header: "Category",
      sortable: true,
      render: (value: string) => (
        <Badge variant="secondary">{value || 'No Category'}</Badge>
      ),
    },
    {
      key: "utility",
      header: "Utility",
      sortable: true,
      render: (value: string) => (
        <Badge variant="outline">{value || 'No Utility'}</Badge>
      ),
    },
    {
      key: "helpful",
      header: "Helpful Count",
      sortable: true,
    },
    {
      key: "status",
      header: "Status",
      render: (value: string, faq: FAQ) => (
        <div className="flex items-center gap-2">
          <Switch
            checked={faq.status === "active"}
            onCheckedChange={() => handleToggleStatus(faq)}
          />
        </div>
      ),
      width: "w-32",
    },
  ];

  // Define table actions
  const actions = [
    {
      label: "View",
      icon: <Eye className="h-4 w-4" />,
      onClick: (faq: FAQ) => onView(faq),
    },
    {
      label: "Edit",
      icon: <Edit className="h-4 w-4" />,
      onClick: (faq: FAQ) => onEdit(faq),
    },
    {
      label: "Delete",
      icon: <Trash2 className="h-4 w-4" />,
      onClick: (faq: FAQ) => handleDelete(faq),
      disabled: () => isDeletingFaq,
    },
  ];

  return (
    <GenericTable<FAQ>
      data={faqs}
      columns={columns}
      rowKey="id"
      actions={actions}
      actionsType="icons"
      emptyMessage="No FAQs found"
      searchPlaceholder={searchPlaceholder}
      searchParamName={searchParamName}
      searchDebounceMs={searchDebounceMs}
      onSearch={onSearch}
      filterPanel={
        currentFilters && onApplyFilters && onResetFilters ? (
          <FAQFilterPanel
            currentFilters={currentFilters}
            onApplyFilters={onApplyFilters}
            onResetFilters={onResetFilters}
            categoryOptions={categoryOptions}
          />
        ) : undefined
      }
      pagination={pagination}
      onPageChange={onPageChange}
    />
  );
};